/*
 * EU Right of Withdrawal function (Directive 2023/2673, Art. 11a).
 * Two genuine steps (declare -> confirm), client timestamp captured,
 * and a submission posted directly to the Zendesk public requests API
 * (requests.json) to create a ticket. Zendesk then emails the customer the
 * durable-medium acknowledgement. Guest-usable, no login or Worker required.
 * Mirrors the contact form's submission path (contact-form-zendesk.js).
 */
class EUWithdrawal extends HTMLElement {
  constructor() {
    super();
    this.data = null;
    this.submittedAt = null;
  }

  connectedCallback() {
    this.declareForm = this.querySelector('[data-step="declare"]');
    this.confirmForm = this.querySelector('[data-step="confirm"]');
    this.doneEl = this.querySelector('[data-step="done"]');
    this.review = this.querySelector('[data-review]');
    this.reviewDone = this.querySelector('[data-review-done]');

    this.declareForm.addEventListener('submit', this.onDeclare.bind(this));
    this.confirmForm.addEventListener('submit', this.onConfirm.bind(this));
    this.querySelector('[data-back]').addEventListener('click', this.onBack.bind(this));
  }

  // ---- Step 1 -> Step 2 -------------------------------------------------
  onDeclare(e) {
    e.preventDefault();
    const fd = new FormData(this.declareForm);
    const data = {
      name: (fd.get('name') || '').toString().trim(),
      order: (fd.get('order') || '').toString().trim(),
      email: (fd.get('email') || '').toString().trim(),
      phone: (fd.get('phone') || '').toString().trim(),
      orderDate: (fd.get('orderDate') || '').toString().trim(),
      details: (fd.get('details') || '').toString().trim(),
    };

    const missing = [];
    if (!data.name) missing.push('full name');
    if (!data.order) missing.push('order number');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) missing.push('a valid email address');
    if (missing.length) {
      this.showError(this.declareForm, 'Please provide ' + missing.join(', ') + '.');
      return;
    }

    this.clearError(this.declareForm);
    this.data = data;
    this.renderReview(this.review, data, false);
    this.show(this.confirmForm);
    this.confirmForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onBack() {
    this.clearError(this.confirmForm);
    this.show(this.declareForm);
    this.declareForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ---- Step 2: confirm --------------------------------------------------
  async onConfirm(e) {
    e.preventDefault();
    const btn = this.confirmForm.querySelector('[data-confirm]');
    if (!btn.dataset.label) btn.dataset.label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Submitting...';
    this.clearError(this.confirmForm);

    this.submittedAt = new Date();
    try {
      await this.sendToZendesk();
    } catch (err) {
      // Do NOT claim success when the request was not recorded. Re-enable the
      // confirm action and tell the customer how to complete their withdrawal.
      console.error('[eu-withdrawal] Zendesk submission failed', err);
      const fallback = this.dataset.fallbackEmail;
      this.showError(
        this.confirmForm,
        'We could not record your withdrawal just now. Please try again' +
          (fallback ? ', or email ' + fallback + ' so we can record it for you.' : '.')
      );
      btn.disabled = false;
      btn.textContent = btn.dataset.label;
      return;
    }

    this.renderReview(this.reviewDone, this.data, true);
    this.querySelector('[data-done-time]').textContent = this.formatTime(this.submittedAt);
    this.querySelector('[data-done-email]').textContent = this.data.email;
    this.show(this.doneEl);
    this.doneEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async sendToZendesk() {
    // Post straight to Zendesk's public request API, exactly like the contact
    // form does. No endpoint means the request cannot be recorded; treat as a
    // failure rather than silently telling the customer it succeeded.
    const subdomain = (this.dataset.zendeskSubdomain || '').trim();
    if (!subdomain) throw new Error('No Zendesk subdomain configured');
    const endpoint = 'https://' + subdomain + '.zendesk.com/api/v2/requests.json';

    const lines = [
      'Statutory withdrawal from contract (EU Directive 2023/2673).',
      '',
      'Name: ' + this.data.name,
      'Order number: ' + this.data.order,
    ];
    if (this.data.phone) lines.push('Phone: ' + this.data.phone);
    if (this.data.orderDate) lines.push('Date of order or receipt: ' + this.data.orderDate);
    if (this.data.details) lines.push('Items or details: ' + this.data.details);
    lines.push('', 'Received at: ' + this.formatTime(this.submittedAt));
    lines.push(
      '',
      'Declaration: I hereby withdraw from the contract for the order identified above, ' +
      'exercising my statutory right of withdrawal.'
    );

    const payload = {
      request: {
        requester: { name: this.data.name, email: this.data.email },
        subject: 'EU withdrawal from contract — order ' + this.data.order,
        comment: { body: lines.join('\n') },
      },
    };

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Zendesk responded ' + res.status);
  }

  // ---- helpers ----------------------------------------------------------
  renderReview(dl, data, done) {
    const rows = [
      ['Full name', data.name],
      ['Order number', data.order],
      ['Email address', data.email],
      ['Phone number', data.phone],
      ['Date of order or receipt', data.orderDate],
      ['Items or details', data.details],
    ];
    if (done) rows.push(['Received at', this.formatTime(this.submittedAt)]);

    dl.innerHTML = rows
      .filter(function (r) { return r[1]; })
      .map(function (r) {
        return '<div class="euw__review-row"><dt>' + r[0] + '</dt><dd>' + escapeHtml(r[1]) + '</dd></div>';
      })
      .join('');
  }

  formatTime(d) {
    // Note: do NOT combine dateStyle/timeStyle with timeZoneName - that throws
    // a RangeError and falls back to the raw Date string. Use explicit fields.
    try {
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(d);
    } catch (e) {
      return d.toString();
    }
  }

  show(stepEl) {
    [this.declareForm, this.confirmForm, this.doneEl].forEach(function (el) {
      el.hidden = el !== stepEl;
    });
    // Move focus to the new step so keyboard and screen-reader users follow
    // the transition rather than being left on a now-hidden control.
    const focusTarget = stepEl.querySelector('[data-step-focus]');
    if (focusTarget) {
      window.requestAnimationFrame(function () {
        focusTarget.focus();
      });
    }
  }

  showError(form, msg) {
    const box = form.querySelector('[data-error]');
    box.textContent = msg;
    box.hidden = false;
  }

  clearError(form) {
    const box = form.querySelector('[data-error]');
    box.textContent = '';
    box.hidden = true;
  }
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

customElements.define('eu-withdrawal', EUWithdrawal);
